import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '../service/auth.service';
import { AppleDeviceService } from '../../services/apple-device.service';
import { AndroidDeviceService } from '../../services/android-device.service';
import { HttpConfigInterceptor } from '../../interciptor/jwt.interceptor';
import Swal from 'sweetalert2';

/**
 * @description Auto-login component for processing secure auto-login tokens.
 * Handles automatic authentication and redirects users to dashboard securely.
 */
@Component({
  selector: 'app-auto-login',
  templateUrl: './auto-login.component.html',
  styleUrls: ['./auto-login.component.scss']
})
export class AutoLoginComponent implements OnInit {

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private spinner: NgxSpinnerService,
    private translate: TranslateService,
    private authService: AuthService,
    private appleDeviceService: AppleDeviceService,
    private androidDeviceService: AndroidDeviceService
  ) { }

  /**
   * @description Angular lifecycle hook called after component initialization.
   * Extracts token from route params and processes secure auto-login.
   */
  ngOnInit(): void {
    this.processAutoLogin();
  }

  /**
   * @description Processes the auto-login by extracting token from URL and authenticating user securely.
   */
  private processAutoLogin(): void {
    this.spinner.show();
    
    // Get token from route parameter
    const token = this.route.snapshot.paramMap.get('token');
    
    if (!token) {
      this.handleError('Invalid or missing authentication token');
      return;
    }

    // أولاً جرب النظام الجديد، وإذا فشل استخدم القديم
    this.validateAutoLoginToken(token);
  }
  
  /**
   * @description Handles legacy auto-login (temporary solution)
   * @param token - The auto-login token
   */
  private handleLegacyAutoLogin(token: string): void {
    try {
      // فك تشفير الـ JWT token محلياً (مؤقت حتى يطبق الـ Backend التغييرات)
      const payload = JSON.parse(atob(token.split('.')[1]));
      
      // التحقق من انتهاء الصلاحية
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp < now) {
        this.handleError('Authentication token has expired');
        return;
      }
      
      // تخزين البيانات مؤقتاً
      this.storeLegacyAuthData(payload, token);
      
      // إعداد اللغة
      if (payload.language) {
        localStorage.setItem('curentLang', payload.language);
        this.translate.setDefaultLang(payload.language);
        this.translate.use(payload.language);
      }
      
      // تنظيف الـ URL
      this.clearTokenFromUrl();
      
      // إظهار النجاح والتوجيه
      this.handleSuccess();
      
    } catch (error) {
      console.error('Legacy auto-login failed:', error);
      this.handleError('Invalid authentication token format');
    }
  }
  
  /**
   * @description Stores legacy auth data (temporary)
   * @param payload - JWT payload
   * @param token - Original token
   */
  private storeLegacyAuthData(payload: any, token: string): void {
    HttpConfigInterceptor.resetSessionExpiredToastFlag();
    // استخدام الـ token الأصلي كـ access token مؤقتاً
    localStorage.setItem('token', token);
    
    // تخزين بيانات المستخدم من الـ payload
    if (payload.userId) {
      localStorage.setItem('user_id', JSON.stringify(payload.userId));
    }
    
    if (payload.merchant_id) {
      localStorage.setItem('merchant_id', JSON.stringify(payload.merchant_id));
    }
    
    if (payload.office_id) {
      localStorage.setItem('office_id', JSON.stringify(payload.office_id));
    }
    
    if (payload.userType) {
      localStorage.setItem('user_type', JSON.stringify(payload.userType));
    }
    
    if (payload.user_name) {
      localStorage.setItem('user_name', payload.user_name);
    }
    
    if (payload.phone_number) {
      localStorage.setItem('phone_number', payload.phone_number);
    }
    
    if (payload.ruleId) {
      localStorage.setItem('ruleId', JSON.stringify(payload.ruleId));
    }
    
    // تحديث حالة المصادقة في الـ AuthService
    this.authService.saveCurrentUser();
    this.authService.isLogin.next(token);
    
    // وضع علامة تسجيل الدخول
    localStorage.setItem('isLoggedIn', 'true');

    this.appleDeviceService.recheckBannerVisibility();
    this.androidDeviceService.recheckBannerVisibility();
  }

  /**
   * @description Securely validates the auto-login token with the backend
   * @param token - The auto-login token to validate
   */
  private validateAutoLoginToken(token: string): void {
    this.authService.validateAutoLoginToken(token).subscribe({
      next: (response) => {
        // Store authentication data
        this.storeAuthenticationData(response);
        
        // Set language if available
        if (response.user?.language) {
          localStorage.setItem('curentLang', response.user.language);
          this.translate.setDefaultLang(response.user.language);
          this.translate.use(response.user.language);
        }

        // Clear the URL to remove the token
        this.clearTokenFromUrl();
        
        // Show success and redirect
        this.handleSuccess();
      },
      error: (error) => {
        console.error('New API failed, falling back to legacy method:', error);
        
        // Fallback إلى النظام القديم
        this.handleLegacyAutoLogin(token);
      }
    });
  }

  /**
   * @description Stores authentication data in localStorage securely.
   * @param {any} authData - Authentication response from backend
   */
  private storeAuthenticationData(authData: any): void {
    HttpConfigInterceptor.resetSessionExpiredToastFlag();
    // Store authentication token
    if (authData.accessToken) {
      localStorage.setItem('token', authData.accessToken);
    }
    
    // Store user information
    if (authData.user) {
      const user = authData.user;
      
      if (user.user_id) {
        localStorage.setItem('user_id', JSON.stringify(user.user_id));
      }
      
      if (user.merchant_id) {
        localStorage.setItem('merchant_id', JSON.stringify(user.merchant_id));
      }
      
      if (user.office_id) {
        localStorage.setItem('office_id', JSON.stringify(user.office_id));
      }
      
      if (user.user_type) {
        localStorage.setItem('user_type', JSON.stringify(user.user_type));
      }

      this.appleDeviceService.recheckBannerVisibility();
      this.androidDeviceService.recheckBannerVisibility();

      if (user.user_name) {
        localStorage.setItem('user_name', user.user_name);
      }
      
      if (user.phone_number) {
        localStorage.setItem('phone_number', user.phone_number);
      }
      
      if (user.rule_id) {
        localStorage.setItem('ruleId', JSON.stringify(user.rule_id));
      }
    }

    // Mark user as logged in
    localStorage.setItem('isLoggedIn', 'true');
  }

  /**
   * @description Clears the token from the URL for security
   */
  private clearTokenFromUrl(): void {
    // Replace the current state to remove token from URL
    window.history.replaceState({}, document.title, '/dashboard');
  }

  /**
   * @description Handles successful authentication.
   */
  private handleSuccess(): void {
    this.spinner.hide();
    
    Swal.fire({
      icon: 'success',
      title: 'تم تسجيل الدخول بنجاح',
      text: 'سيتم توجيهك إلى لوحة التحكم...',
      timer: 2000,
      showConfirmButton: false
    }).then(() => {
      // Redirect to dashboard
      this.router.navigate(['/dashboard']);
    });
  }

  /**
   * @description Handles authentication errors.
   * @param {string} message - Error message to display
   */
  private handleError(message: string): void {
    this.spinner.hide();
    
    Swal.fire({
      icon: 'error',
      title: 'خطأ في المصادقة',
      text: 'فشل في تسجيل الدخول. قد يكون الرابط منتهي الصلاحية.',
      confirmButtonText: 'موافق'
    }).then(() => {
      // Redirect to login page
      this.router.navigate(['/login']);
    });
  }
}